/**
 * Support chat backend: backend-dashboard `src/modules/supportChat`, mounted
 * at `{API base}/v1/support` (the API base already ends in `/api`).
 */
export const SUPPORT_API = {
  /** REST, relative to the API base (BaseUrlInterceptor adds it). */
  basePath: '/v1/support',
  /** Socket.IO path, appended to the API base's path (`/api`). */
  socketPath: '/v1/support/socket.io',
  /** How long to wait for a REST answer or a socket ack. */
  timeoutMs: 10_000,
  /** Sending an issue report (it turns nudges off before answering). */
  reportTimeoutMs: 30_000,
  /** Messages per page (backend allows 1–100). */
  pageSize: 50,
  /**
   * Safety net for missed live events (e.g. a backend worker without the
   * Redis fan-out): the open conversation re-checks over REST this often…
   */
  threadSyncMs: 8_000,
  /** …and the conversation list (assignments, closes, new chats) this often. */
  listSyncMs: 30_000,
};

export const SUPPORT_SOCKET_EVENTS = {
  /** client → server, ack: chat summary. Joins the chat's room. */
  join: 'chat:join',
  /** client → server, ack: null */
  leave: 'chat:leave',
  /** server → client: SupportApiMessage (also echoes our own sends) */
  message: 'chat:message',
  /** server → client: SupportApiChat (a manager took the chat over) */
  assigned: 'chat:assigned',
  /** server → client: SupportApiChat (status 'closed') */
  closed: 'chat:closed',
  /* Team inbox (managers and CS only). */
  /** client → server, ack: null. Summaries of every chat from now on. */
  subscribeInbox: 'chat:subscribe-inbox',
  /** server → inbox: SupportApiChat (a client opened a chat) */
  created: 'chat:created',
  /** server → inbox: SupportApiChat (new message: preview, time, count) */
  updated: 'chat:updated',
} as const;

/**
 * Testing in production: the client widget (chat, "Report an issue", the
 * chat nudge) only starts in browsers where localStorage `show_support` is
 * `'true'`, so other clients don't see it yet. Remove for the launch.
 */
export const SUPPORT_SHOW_KEY = 'show_support';

export function supportShownInThisBrowser(): boolean {
  try {
    return localStorage.getItem(SUPPORT_SHOW_KEY) === 'true';
  } catch {
    return false;
  }
}

/** Parts of the chat-box that can be switched off. */
export const SUPPORT_FEATURES = {
  /**
   * "Report an issue": a form in a modal, sent to `/issue-reports` and
   * answered by email (it is not a chat).
   */
  issueReports: true,
  /**
   * Files on chat messages. Also hidden at runtime if the backend answers
   * that uploads aren't configured (503).
   */
  attachments: true,
};

/** Same rules as the backend; checked before reserving for instant feedback. */
export const SUPPORT_ATTACHMENT_LIMITS = {
  maxFiles: 10,
  maxBytes: 25 * 1024 * 1024,
  mimeTypes: [
    // Images
    'image/png',
    'image/jpeg',
    'image/gif',
    'image/webp',
    'image/avif',
    // Documents
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.oasis.opendocument.text',
    // Spreadsheets
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.oasis.opendocument.spreadsheet',
    'text/csv',
    'text/tab-separated-values',
    // Presentations
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.oasis.opendocument.presentation',
    // Text
    'text/plain',
    'application/json',
    // Video
    'video/mp4',
    'video/webm',
    'video/quicktime',
    // Audio
    'audio/mpeg',
    'audio/wav',
    'audio/ogg',
    'audio/mp4',
  ],
};

/** Ring-buffer sizes and payload caps for the issue-report snapshot. */
export const SUPPORT_DIAGNOSTICS_LIMITS = {
  maxRequests: 60,
  maxNavigation: 40,
  maxBodyChars: 1500,
  sessionStorageKey: 'bhm-support-activity',
};
